import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, Save, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { formatDate, formatNumber, formatRM, METRIC_KEYS, METRIC_LABELS } from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import { ReportDownload } from "@/components/portal/ReportDownload";
import { PlatformBadge } from "@/components/portal/PlatformBits";
import { AdjustedBadge, CorrectionModal } from "@/components/portal/CorrectionBits";
import {
  Badge, Button, Card, Empty, ErrorNote, Field, Loading, Modal, Note, inputClass,
} from "@/components/portal/Ui";
import { SubmissionsSection } from "@/components/portal/SubmissionsSection";
import { InvoicesSection } from "@/components/portal/InvoicesSection";

const DELIVERY_TONE = {
  Delivered: "green",
  "Over-delivered": "green",
  "Partially delivered": "orange",
  "Nothing posted": "red",
  "Not required": "neutral",
};
const INSIGHT_TONE = {
  Reported: "green",
  Missing: "orange",
  "Needs correction": "red",
  "No posts yet": "neutral",
  "Not required": "neutral",
};
const INVOICE_TONE = {
  "None submitted": "neutral",
  Submitted: "blue",
  "Changes requested": "red",
  Approved: "green",
  Paid: "green",
};

function Metric({ label, value, hint, testId }) {
  return (
    <Card testId={testId}>
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">{label}</p>
      <p className="mt-2 text-3xl font-extrabold tabular">{value}</p>
      {hint && <p className="mt-1.5 text-xs leading-snug body-text">{hint}</p>}
    </Card>
  );
}

function StickyNoteEditor({ campaignId, note, onSaved }) {
  const [body, setBody] = useState(note?.body || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBody(note?.body || "");
  }, [note]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/campaigns/${campaignId}/note`, { body });
      toast.success(body.trim() ? "Sticky note saved" : "Sticky note cleared");
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId="campaign-note-card">
      <div className="flex items-center gap-2">
        <StickyNote className="h-4 w-4 text-[#A85A05]" />
        <h2 className="text-sm font-extrabold">Sticky note</h2>
      </div>
      <textarea
        rows={2}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="One pinned reminder for the team on this campaign."
        className={`${inputClass} mt-2.5`}
        data-testid="campaign-note-input"
      />
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs body-text">
          {note?.author_name
            ? `Last edited by ${note.author_name}`
            : "Internal only — never shown to clients or KOLs."}
        </p>
        <Button busy={busy} onClick={save} testId="campaign-note-save">
          <Save className="h-4 w-4" />
          Save note
        </Button>
      </div>
    </Card>
  );
}

function WaiverModal({ state, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason("");
  }, [state]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post(
        `/campaigns/${state.campaignId}/creators/${state.creator.creator_id}/invoice-waiver`,
        { reason: reason.trim() }
      );
      toast.success("Invoice requirement waived for this KOL");
      await onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(state)}
      onClose={() => !busy && onClose()}
      title="Waive the invoice requirement"
      testId="waiver-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Note tone="orange">
          {state?.creator.creator_name} will be able to invoice this campaign even though their
          reporting is incomplete. The KOL only sees the campaign become invoiceable; the reason
          stays internal and is logged.
        </Note>
        <Field label="Why are you waiving it?" required>
          <input
            required
            minLength={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputClass}
            placeholder="e.g. Post taken down by the brand after going live"
            data-testid="waiver-reason-input"
          />
        </Field>
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} disabled={busy} testId="waiver-cancel">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="waiver-submit">
            Waive requirement
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function CorrectionPicker({ state, onClose, onCorrect }) {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    if (!state) return;
    setPosts(null);
    api
      .get("/posts", {
        params: { campaign_id: state.campaignId, creator_id: state.creator.creator_id },
      })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, [state]);

  return (
    <Modal
      open={Boolean(state)}
      onClose={onClose}
      title={`Correct a submission by ${state?.creator.creator_name || ""}`}
      wide
      testId="correction-picker-modal"
    >
      {posts === null ? (
        <Loading />
      ) : posts.length === 0 ? (
        <p className="text-sm body-text">This KOL has no submissions on this campaign yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {posts.map((post) => (
            <li
              key={post.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#EFEAE1] p-3.5"
              data-testid={`picker-post-${post.id}`}
            >
              <div>
                <p className="text-sm font-semibold">
                  {post.platform} · {formatDate(post.posting_date)}
                  {post.is_adjusted && (
                    <span className="ml-2">
                      <AdjustedBadge post={post} testId={`picker-adjusted-${post.id}`} />
                    </span>
                  )}
                </p>
                <p className="mt-0.5 max-w-lg truncate text-xs body-text">{post.url}</p>
              </div>
              <Button variant="outline" onClick={() => onCorrect(post)} testId={`picker-correct-${post.id}`}>
                Correct
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

const SECTIONS = [
  { id: "summary", label: "Summary" },
  { id: "kols", label: "KOLs" },
  { id: "submissions", label: "Content submissions" },
  { id: "invoices", label: "Invoices" },
];

function JumpBar({ onJump, active }) {
  return (
    <nav
      className="sticky top-[112px] z-20 -mx-1 mb-5 flex flex-wrap gap-1.5 rounded-full border border-[#E6E1D8] bg-white/90 p-1.5 backdrop-blur"
      data-testid="campaign-jump-bar"
    >
      {SECTIONS.map((section) => (
        <button
          key={section.id}
          type="button"
          onClick={() => onJump(section.id)}
          aria-current={active === section.id}
          data-testid={`campaign-jump-${section.id}`}
          className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
            active === section.id
              ? "bg-[#111111] text-white"
              : "text-[#6b6660] hover:text-[#111111]"
          }`}
        >
          {section.label}
        </button>
      ))}
    </nav>
  );
}

function Section({ id, title, count, open, onToggle, children, innerRef }) {
  return (
    <Card testId={`campaign-section-${id}`}>
      <div ref={innerRef} className="scroll-mt-44" />
      <button
        type="button"
        onClick={onToggle}
        data-testid={`campaign-section-toggle-${id}`}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <h2 className="text-lg font-extrabold">
          {title}
          {count != null && <span className="ml-2 text-sm font-semibold body-text">{count}</span>}
        </h2>
        {open ? (
          <ChevronUp className="h-4 w-4 text-[#6b6660]" />
        ) : (
          <ChevronDown className="h-4 w-4 text-[#6b6660]" />
        )}
      </button>
      {open ? <div className="mt-4">{children}</div> : null}
    </Card>
  );
}

export default function TrackerCampaign() {
  const { campaignId } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [waiverTarget, setWaiverTarget] = useState(null);
  const [pickerTarget, setPickerTarget] = useState(null);
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [drive, setDrive] = useState(null);
  const refs = { summary: useRef(null), kols: useRef(null), submissions: useRef(null),
                 invoices: useRef(null) };
  const focus = params.get("focus") || "";
  const closed = (params.get("closed") || "").split(",").filter(Boolean);

  // setSearchParams does not queue like setState — several keys must change in ONE call
  const setParamsMany = (updates) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        return next;
      },
      { replace: true }
    );
  const setParam = (key, value) => setParamsMany({ [key]: value });

  const toggleSection = (id) => {
    const next = closed.includes(id) ? closed.filter((s) => s !== id) : [...closed, id];
    setParam("closed", next.join(","));
  };

  const jump = (id, extra = {}) => {
    const updates = { ...extra, focus: id };
    if (closed.includes(id)) updates.closed = closed.filter((s) => s !== id).join(",");
    setParamsMany(updates);
    refs[id]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await api.get(`/internal/tracker/campaign/${campaignId}`);
      setData(response.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setRefreshing(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
    api.get("/drive/status").then((r) => setDrive(r.data)).catch(() => setDrive(null));
  }, [load]);

  useEffect(() => {
    if (!focus || !data) return;
    const node = refs[focus]?.current;
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    // the sections below load their own rows, so settle the position once they are in
    const again = setTimeout(() => {
      refs[focus]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 900);
    return () => clearTimeout(again);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, Boolean(data)]);

  if (!data)
    return error ? (
      <div data-testid="campaign-view-error">
        <ErrorNote message={error} onRetry={load} />
        <Link
          to="/internal/tracker"
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F2811D]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Campaigns Tracker
        </Link>
      </div>
    ) : (
      <Loading label="Compiling campaign totals…" />
    );

  const { campaign, totals, platforms, coverage, pending_approval: pending, at_risk: risk,
    delivery, money, creators } = data;

  return (
    <div data-testid="tracker-campaign-page">
      <Link
        to="/internal/tracker"
        className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F2811D]"
        data-testid="campaign-back-link"
      >
        <ArrowLeft className="h-4 w-4" /> Campaigns Tracker
      </Link>

      <PageHeader
        title={campaign.name}
        description={`${campaign.client_org_name} · ${
          campaign.event_date
            ? `event ${formatDate(campaign.event_date)}`
            : `${formatDate(campaign.start_date)} – ${formatDate(campaign.end_date)}`
        }${campaign.category ? ` · ${campaign.category}` : ""}`}
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ReportDownload campaignId={campaignId} testId="campaign-report-download" />
            {isAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const next = campaign.client_visible === false;
                    await api.put(`/campaigns/${campaignId}/visibility`, { client_visible: next });
                    toast.success(
                      next
                        ? "Campaign is now visible to the client"
                        : "Campaign hidden from the client's list"
                    );
                    await load();
                  } catch (e) {
                    toast.error(apiError(e));
                  }
                }}
                testId="campaign-toggle-visibility"
              >
                {campaign.client_visible === false ? "Show to client" : "Hide from client"}
              </Button>
            )}
            <Badge testId="campaign-status">{campaign.status}</Badge>
          </div>
        }
      />
      {campaign.client_visible === false && (
        <div className="mb-4">
          <Note tone="neutral" testId="campaign-hidden-note">
            Hidden from the client&apos;s campaign list. A client holding a direct link can still
            open this campaign and export its report.
          </Note>
        </div>
      )}
      <ErrorNote message={error} onRetry={load} />

      <JumpBar onJump={jump} active={focus || "summary"} />

      <div className="space-y-5">
        <div ref={refs.summary} className="scroll-mt-44" />
        <StickyNoteEditor campaignId={campaignId} note={campaign.sticky_note} onSaved={load} />

        {campaign.is_ugc ? (
          <Note tone="neutral" testId="campaign-ugc-note">
            This is a UGC — Content Creation campaign, so posting links and insights are not
            required. Delivery and invoice state are tracked below.
          </Note>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                label="Total reach"
                value={formatNumber(totals.reach)}
                hint={`reported by ${coverage.reach.reported} of ${coverage.reach.total} posts`}
                testId="campaign-total-reach"
              />
              <Metric
                label="Total views"
                value={formatNumber(totals.views)}
                hint={`reported by ${coverage.views.reported} of ${coverage.views.total} posts`}
                testId="campaign-total-views"
              />
              <Metric
                label="Total engagements"
                value={formatNumber(totals.engagements)}
                hint="Likes, comments, shares and saves"
                testId="campaign-total-engagements"
              />
              <Metric
                label="Engagement rate"
                value={totals.engagement_rate == null ? "—" : `${totals.engagement_rate}%`}
                hint={
                  totals.rate_basis
                    ? `Engagements over total ${totals.rate_basis}`
                    : "No reach or views reported yet"
                }
                testId="campaign-engagement-rate"
              />
            </div>

            {(pending.posts > 0 || risk.posts > 0) && (
              <div className="grid gap-4 md:grid-cols-2">
                {pending.posts > 0 && (
                  <Note tone="orange" testId="campaign-pending-note">
                    <strong>{pending.posts}</strong> submission
                    {pending.posts === 1 ? "" : "s"} still awaiting internal approval are included
                    in these totals — {formatNumber(pending.reach)} reach and{" "}
                    {formatNumber(pending.engagements)} engagements the client cannot see yet.
                  </Note>
                )}
                {risk.posts > 0 && (
                  <Note tone="red" testId="campaign-at-risk-note">
                    <strong>{risk.posts}</strong> insight
                    {risk.posts === 1 ? "" : "s"} marked "needs correction" are excluded from every
                    total above — {formatNumber(risk.reach)} reach and{" "}
                    {formatNumber(risk.engagements)} engagements at risk.
                  </Note>
                )}
              </div>
            )}

            <Card testId="campaign-platform-card">
              <h2 className="text-lg font-extrabold">Per-platform breakdown</h2>
              {platforms.length === 0 ? (
                <p className="mt-3 text-sm body-text">
                  No reported insights yet, so there is nothing to break down.
                </p>
              ) : (
                <div className="scroll-x mt-3">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                        <th className="pb-2.5 pr-4">Platform</th>
                        <th className="pb-2.5 pr-4 text-right">Posts</th>
                        <th className="pb-2.5 pr-4 text-right">Reach</th>
                        <th className="pb-2.5 pr-4 text-right">Views</th>
                        <th className="pb-2.5 pr-4 text-right">Engagements</th>
                        <th className="pb-2.5 pr-4 text-right">Eng. rate</th>
                        <th className="pb-2.5 text-right">Share of engagements</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platforms.map((row) => (
                        <tr
                          key={row.platform}
                          className="border-b border-[#F5F1EA]"
                          data-testid={`campaign-platform-${row.platform}`}
                        >
                          <td className="py-3 pr-4">
                            <PlatformBadge platform={row.platform} />
                          </td>
                          <td className="py-3 pr-4 text-right tabular">{formatNumber(row.posts)}</td>
                          <td className="py-3 pr-4 text-right tabular">{formatNumber(row.reach)}</td>
                          <td className="py-3 pr-4 text-right tabular">{formatNumber(row.views)}</td>
                          <td className="py-3 pr-4 text-right tabular">
                            {formatNumber(row.engagements)}
                          </td>
                          <td className="py-3 pr-4 text-right tabular">
                            {row.engagement_rate == null ? "—" : `${row.engagement_rate}%`}
                          </td>
                          <td className="py-3 text-right tabular">
                            {row.engagement_share == null ? "—" : `${row.engagement_share}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs body-text" data-testid="campaign-coverage">
                {METRIC_KEYS.map((key) => (
                  <span key={key}>
                    {METRIC_LABELS[key]} reported by {coverage[key].reported} of{" "}
                    {coverage[key].total} posts
                  </span>
                ))}
              </div>
            </Card>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Metric
            label="KOLs assigned"
            value={formatNumber(delivery.creators_count)}
            hint={`${formatNumber(delivery.published_posts)} of ${formatNumber(delivery.expected_posts)} expected posts published${
              delivery.expected_posts > 0 && delivery.published_posts > delivery.expected_posts
                ? " · over-delivered"
                : ""
            }`}
            testId="campaign-delivery-kols"
          />
          <Metric
            label="Total invoiced"
            value={formatRM(money.invoiced_sen)}
            hint={`${money.submitted} submitted · ${money.approved} approved · ${money.paid} paid`}
            testId="campaign-invoiced"
          />
          <Metric
            label="Still unpaid"
            value={formatRM(money.unpaid_sen)}
            hint={money.changes_requested ? `${money.changes_requested} awaiting correction` : "Across every unpaid invoice"}
            testId="campaign-unpaid"
          />
        </div>

        <div ref={refs.kols} className="scroll-mt-44" />
        <Card testId="campaign-creators-card">
          <h2 className="text-lg font-extrabold">KOLs in this campaign</h2>
          {creators.length === 0 ? (
            <Empty
              testId="campaign-creators-empty"
              title="No KOLs assigned yet"
              body="Assign creators from the Campaigns page to start tracking delivery."
            />
          ) : (
            <div className="scroll-x mt-3">
              <table className="w-full min-w-[1080px] text-sm">
                <thead>
                  <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                    <th className="pb-2.5 pr-4">KOL</th>
                    <th className="pb-2.5 pr-4">Posts</th>
                    <th className="pb-2.5 pr-4">Performance</th>
                    <th className="pb-2.5 pr-4">Insights</th>
                    <th className="pb-2.5 pr-4 text-right">Reach</th>
                    <th className="pb-2.5 pr-4 text-right">Views</th>
                    <th className="pb-2.5 pr-4 text-right">Engagements</th>
                    <th className="pb-2.5 pr-4">Invoice</th>
                    <th className="pb-2.5">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {creators.map((creator) => (
                    <tr
                      key={creator.creator_id}
                      className={`border-b border-[#F5F1EA] align-top ${creator.outstanding ? "bg-[#FFFDF6]" : ""}`}
                      data-testid={`campaign-creator-${creator.creator_id}`}
                    >
                      <td className="py-3 pr-4">
                        <p className="font-semibold">
                          {creator.creator_name}
                          {creator.outstanding && (
                            <AlertTriangle
                              className="ml-1.5 inline h-3.5 w-3.5 text-[#A85A05]"
                              data-testid={`creator-outstanding-${creator.creator_id}`}
                            />
                          )}
                        </p>
                        <p className="mt-0.5 text-xs body-text">
                          {(creator.socials || []).map((s) => s.handle).join(" · ") || "No handle on file"}
                        </p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="tabular">
                          {formatNumber(creator.published_posts)} / {formatNumber(creator.expected_posts)}
                          {creator.expected_posts_overridden && (
                            <span
                              className="ml-2 rounded-full bg-[#EDF2FB] px-2 py-0.5 text-[11px] font-bold text-[#2C4D8F]"
                              data-testid={`creator-overridden-${creator.creator_id}`}
                            >
                              Hand-set
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs body-text">
                          {creator.platforms.length ? creator.platforms.join(", ") : "—"}
                        </p>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          tone={DELIVERY_TONE[creator.delivery_status]}
                          testId={`creator-delivery-${creator.creator_id}`}
                        >
                          {creator.delivery_status}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          tone={INSIGHT_TONE[creator.insight_status]}
                          testId={`creator-insight-${creator.creator_id}`}
                        >
                          {creator.insight_status}
                        </Badge>
                        <p className="mt-1 text-[11px] body-text">
                          Posting {formatDate(creator.posting_deadline)} · Insights{" "}
                          {formatDate(creator.insight_deadline)}
                        </p>
                      </td>
                      <td className="py-3 pr-4 text-right tabular">{formatNumber(creator.reach)}</td>
                      <td className="py-3 pr-4 text-right tabular">{formatNumber(creator.views)}</td>
                      <td className="py-3 pr-4 text-right tabular">
                        {formatNumber(creator.engagements)}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          tone={INVOICE_TONE[creator.invoice_status]}
                          testId={`creator-invoice-${creator.creator_id}`}
                        >
                          {creator.invoice_status}
                        </Badge>
                        {creator.invoice_amount_sen != null && (
                          <p className="mt-1 text-xs font-semibold tabular">
                            {formatRM(creator.invoice_amount_sen)}
                          </p>
                        )}
                        {creator.invoice_waived ? (
                          <p
                            className="mt-1 text-[11px] font-semibold text-[#2C4D8F]"
                            data-testid={`creator-waived-${creator.creator_id}`}
                          >
                            Gate waived — {creator.invoice_waiver_reason}
                          </p>
                        ) : creator.invoice_ready ? (
                          <p
                            className="mt-1 text-[11px] font-semibold text-[#1B6B3A]"
                            data-testid={`creator-ready-${creator.creator_id}`}
                          >
                            Ready to invoice
                          </p>
                        ) : (
                          <>
                            <p
                              className="mt-1 text-[11px] font-semibold text-[#A85A05]"
                              data-testid={`creator-blocked-${creator.creator_id}`}
                            >
                              Cannot invoice: {creator.invoice_blockers.join("; ")}
                            </p>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => setWaiverTarget({ campaignId, creator })}
                                data-testid={`waive-gate-${creator.creator_id}`}
                                className="mt-1 text-[11px] font-semibold text-[#2C4D8F] hover:underline"
                              >
                                Waive requirement
                              </button>
                            )}
                          </>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col gap-1">
                          <Link
                            to={`?creator=${creator.creator_id}&focus=submissions`}
                            onClick={(event) => {
                              if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                              event.preventDefault();
                              jump("submissions", { creator: creator.creator_id });
                            }}
                            data-testid={`creator-open-posts-${creator.creator_id}`}
                            className="inline-flex items-center gap-1.5 text-left text-xs font-semibold text-[#F2811D] hover:underline"
                          >
                            Submissions
                          </Link>
                          {creator.invoice_id && (
                            <Link
                              to={`?inv_creator=${creator.creator_id}&focus=invoices`}
                              onClick={(event) => {
                                if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                                event.preventDefault();
                                jump("invoices", {
                                  inv_creator: creator.creator_id,
                                  inv_status: "",
                                });
                              }}
                              data-testid={`creator-open-invoice-${creator.creator_id}`}
                              className="inline-flex items-center gap-1.5 text-left text-xs font-semibold text-[#F2811D] hover:underline"
                            >
                              Invoice
                            </Link>
                          )}
                          {isAdmin && creator.published_posts > 0 && (
                            <button
                              type="button"
                              onClick={() => setPickerTarget({ campaignId, creator })}
                              data-testid={`creator-correct-${creator.creator_id}`}
                              className="text-left text-xs font-semibold text-[#2C4D8F] hover:underline"
                            >
                              Correct submission
                            </button>
                          )}
                          {creator.adjusted_posts > 0 && (
                            <span
                              className="text-[11px] font-semibold text-[#2C4D8F]"
                              data-testid={`creator-adjusted-${creator.creator_id}`}
                            >
                              {creator.adjusted_posts} adjusted
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-xs body-text">
            Rows with something outstanding are tinted and flagged. Internal remarks never appear in
            client-facing output; deleted submissions and invoices are excluded from every total on
            this page.
          </p>
        </Card>

        <Section
          id="submissions"
          title="Content submissions"
          innerRef={refs.submissions}
          open={!closed.includes("submissions")}
          onToggle={() => toggleSection("submissions")}
          count={`${formatNumber(delivery.published_posts)} on record`}
        >
          <SubmissionsSection campaign={campaign} creators={creators} onChanged={load} />
        </Section>

        <Section
          id="invoices"
          title="Invoices"
          innerRef={refs.invoices}
          open={!closed.includes("invoices")}
          onToggle={() => toggleSection("invoices")}
          count={`${formatRM(money.invoiced_sen)} invoiced`}
        >
          <InvoicesSection
            campaign={campaign}
            creators={creators}
            drive={drive}
            onChanged={load}
          />
        </Section>
      </div>

      <WaiverModal state={waiverTarget} onClose={() => setWaiverTarget(null)} onSaved={load} />
      <CorrectionPicker
        state={pickerTarget}
        onClose={() => setPickerTarget(null)}
        onCorrect={(post) => {
          setPickerTarget(null);
          setCorrectionTarget(post);
        }}
      />
      <CorrectionModal
        post={correctionTarget}
        onClose={() => setCorrectionTarget(null)}
        onSaved={async () => {
          setCorrectionTarget(null);
          await load();
        }}
      />
    </div>
  );
}
